import api from './api';
import { ApiResponse } from '../types/api';
import type {
  CoinsPurchaseResult,
  PaginatedTransactionHistory,
  RedeemWalletCodeResult,
  Transaction,
  StudentTransactionHistory,
  WalletChargeResponse,
} from '../types/wallet';

function getErrorMessage(body: ApiResponse<unknown>, fallback: string): string {
  if (typeof body.message === 'string' && body.message) return body.message;
  if (typeof body.data === 'string' && body.data) return body.data;
  return fallback;
}

function unwrapPayload<T>(body: ApiResponse<unknown>): T | null {
  if (!body.success) return null;
  const message = body.message;
  const data = body.data;
  if (message && typeof message === 'object') return message as T;
  if (data && typeof data === 'object') return data as T;
  return null;
}

function normalizeTransaction(raw: Record<string, unknown>): Transaction {
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    studentId: String(raw.studentId ?? raw.StudentId ?? ''),
    studentName: String(raw.studentName ?? raw.StudentName ?? ''),
    type: String(raw.type ?? raw.Type ?? ''),
    amount: Number(raw.amount ?? raw.Amount ?? 0),
    balanceAfter: Number(raw.balanceAfter ?? raw.BalanceAfter ?? 0),
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
    description: (raw.description ?? raw.Description ?? undefined) as string | undefined,
    paymentGatewayOrderId: (raw.paymentGatewayOrderId ?? raw.PaymentGatewayOrderId ?? undefined) as string | undefined,
    paymentGatewayTransactionId: (raw.paymentGatewayTransactionId ?? raw.PaymentGatewayTransactionId ?? undefined) as string | undefined,
    walletCodeId: (raw.walletCodeId ?? raw.WalletCodeId ?? undefined) as string | undefined,
    walletCodeCode: (raw.walletCodeCode ?? raw.WalletCodeCode ?? undefined) as string | undefined,
    itemId: raw.itemId != null || raw.ItemId != null ? String(raw.itemId ?? raw.ItemId) : undefined,
    itemType: (raw.itemType ?? raw.ItemType ?? undefined) as Transaction['itemType'],
    itemPrice: raw.itemPrice != null || raw.ItemPrice != null ? Number(raw.itemPrice ?? raw.ItemPrice) : undefined,
    teacherId: raw.teacherId != null || raw.TeacherId != null ? String(raw.teacherId ?? raw.TeacherId) : undefined,
    teacherName: (raw.teacherName ?? raw.TeacherName ?? undefined) as string | undefined,
  };
}

function normalizeHistory(raw: Record<string, unknown>): StudentTransactionHistory {
  const transactionsRaw = (raw.transactions ?? raw.Transactions ?? []) as Record<string, unknown>[];
  return {
    studentId: String(raw.studentId ?? raw.StudentId ?? ''),
    studentName: String(raw.studentName ?? raw.StudentName ?? ''),
    currentBalance: Number(raw.currentBalance ?? raw.CurrentBalance ?? 0),
    totalTransactions: Number(raw.totalTransactions ?? raw.TotalTransactions ?? 0),
    transactions: Array.isArray(transactionsRaw) ? transactionsRaw.map(normalizeTransaction) : [],
  };
}

function normalizeCoinsPurchase(raw: Record<string, unknown>): CoinsPurchaseResult {
  return {
    courseName: (raw.courseName ?? raw.CourseName ?? undefined) as string | undefined,
    unitName: (raw.unitName ?? raw.UnitName ?? undefined) as string | undefined,
    coinsSpent: Number(raw.coinsSpent ?? raw.CoinsSpent ?? 0),
    previousBalance: Number(raw.previousBalance ?? raw.PreviousBalance ?? 0),
    newBalance: Number(raw.newBalance ?? raw.NewBalance ?? 0),
    transactionId: String(raw.transactionId ?? raw.TransactionId ?? ''),
  };
}

function normalizeWalletCharge(raw: Record<string, unknown>): WalletChargeResponse {
  const orderRaw = (raw.walletChargeOrder ?? raw.WalletChargeOrder ?? {}) as Record<string, unknown>;
  const sessionRaw = (raw.paymentSession ?? raw.PaymentSession ?? {}) as Record<string, unknown>;

  return {
    walletChargeOrder: {
      id: String(orderRaw.id ?? orderRaw.Id ?? ''),
      studentId: String(orderRaw.studentId ?? orderRaw.StudentId ?? ''),
      studentName: String(orderRaw.studentName ?? orderRaw.StudentName ?? ''),
      amount: Number(orderRaw.amount ?? orderRaw.Amount ?? 0),
      coinsAmount: Number(orderRaw.coinsAmount ?? orderRaw.CoinsAmount ?? 0),
      orderDate: String(orderRaw.orderDate ?? orderRaw.OrderDate ?? ''),
      status: String(orderRaw.status ?? orderRaw.Status ?? ''),
      paymentReference: (orderRaw.paymentReference ?? orderRaw.PaymentReference ?? undefined) as string | undefined,
      paymentDate: (orderRaw.paymentDate ?? orderRaw.PaymentDate ?? undefined) as string | undefined,
    },
    paymentSession: {
      sessionId: String(sessionRaw.sessionId ?? sessionRaw.SessionId ?? ''),
      sessionUrl: String(sessionRaw.sessionUrl ?? sessionRaw.SessionUrl ?? ''),
      walletChargeOrderId: String(sessionRaw.walletChargeOrderId ?? sessionRaw.WalletChargeOrderId ?? ''),
      amount: Number(sessionRaw.amount ?? sessionRaw.Amount ?? 0),
      coinsAmount: Number(sessionRaw.coinsAmount ?? sessionRaw.CoinsAmount ?? 0),
      currency: String(sessionRaw.currency ?? sessionRaw.Currency ?? 'EGP'),
      expiresAt: String(sessionRaw.expiresAt ?? sessionRaw.ExpiresAt ?? ''),
    },
  };
}

export const walletService = {
  async getMyHistory(pageNumber = 1, pageSize = 10) {
    try {
      const response = await api.get<ApiResponse<unknown>>('/api/Transaction/my-history', {
        params: { pageNumber, pageSize },
      });
      const body = response.data;
      const payload = unwrapPayload<Record<string, unknown>>(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'تعذر تحميل سجل المحفظة') };
      }

      const historyRaw = (payload.data ?? payload.Data ?? payload) as Record<string, unknown>;
      const history = normalizeHistory(historyRaw);

      return {
        success: true as const,
        data: {
          history,
          totalCount: Number(payload.totalCount ?? payload.TotalCount ?? history.totalTransactions),
          pageNumber: Number(payload.pageNumber ?? payload.PageNumber ?? pageNumber),
          pageSize: Number(payload.pageSize ?? payload.PageSize ?? pageSize),
          totalPages: Number(payload.totalPages ?? payload.TotalPages ?? 1),
        } satisfies PaginatedTransactionHistory,
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const message = err.response?.data ? getErrorMessage(err.response.data, 'تعذر تحميل سجل المحفظة') : 'تعذر تحميل سجل المحفظة';
      return { success: false as const, error: message };
    }
  },

  async redeemWalletCode(code: string) {
    try {
      const response = await api.post<ApiResponse<unknown>>('/api/WalletCode/redeem', { code: code.trim() });
      const body = response.data;
      const payload = unwrapPayload<Record<string, unknown>>(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'فشل في استخدام كود المحفظة') };
      }

      const result: RedeemWalletCodeResult = {
        previousBalance: Number(payload.previousBalance ?? payload.PreviousBalance ?? 0),
        addedCoins: Number(payload.addedCoins ?? payload.AddedCoins ?? 0),
        newBalance: Number(payload.newBalance ?? payload.NewBalance ?? 0),
        transactionId: String(payload.transactionId ?? payload.TransactionId ?? ''),
      };

      return { success: true as const, data: result };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const message = err.response?.data ? getErrorMessage(err.response.data, 'فشل في استخدام كود المحفظة') : 'فشل في استخدام كود المحفظة';
      return { success: false as const, error: message };
    }
  },

  async chargeWallet(amount: number, currency = 'EGP') {
    try {
      const response = await api.post<ApiResponse<unknown>>('/api/Payment/charge-wallet', { amount, currency });
      const body = response.data;
      const payload = unwrapPayload<Record<string, unknown>>(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'فشل في إنشاء جلسة الشحن') };
      }
      return { success: true as const, data: normalizeWalletCharge(payload) };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const message = err.response?.data ? getErrorMessage(err.response.data, 'فشل في إنشاء جلسة الشحن') : 'فشل في إنشاء جلسة الشحن';
      return { success: false as const, error: message };
    }
  },

  async buyCourseWithCoins(courseId: string, promoCode?: string) {
    try {
      const requestBody: { courseId: string; promoCode?: string } = { courseId };
      if (promoCode?.trim()) {
        requestBody.promoCode = promoCode.trim();
      }
      const response = await api.post<ApiResponse<unknown>>('/api/Coins/buy-course', requestBody);
      const body = response.data;
      const payload = unwrapPayload<Record<string, unknown>>(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'فشل في شراء الكورس') };
      }
      return { success: true as const, data: normalizeCoinsPurchase(payload) };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const message = err.response?.data ? getErrorMessage(err.response.data, 'فشل في شراء الكورس') : 'فشل في شراء الكورس';
      return { success: false as const, error: message };
    }
  },

  async buyUnitWithCoins(unitId: number) {
    try {
      const response = await api.post<ApiResponse<unknown>>('/api/Coins/buy-unit', { unitId });
      const body = response.data;
      const payload = unwrapPayload<Record<string, unknown>>(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'فشل في شراء الحصة') };
      }
      return { success: true as const, data: normalizeCoinsPurchase(payload) };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      const message = err.response?.data ? getErrorMessage(err.response.data, 'فشل في شراء الحصة') : 'فشل في شراء الحصة';
      return { success: false as const, error: message };
    }
  },
};
